import type {
  CommentListResponse,
  AlbumListResponse,
  SavedAlbumListResponse,
  AlbumPhotoListResponse,
  AlbumDownloadResponse,
  JobStatusResponse,
  ParentCommentSyncResponse,
  ParentCommentListResponse,
  ProfileResponse,
  SyncFamilyResponse,
  CredentialStatusResponse,
  SaveCredentialResponse,
  DeleteCredentialResponse,
  LineNotifyResponse
} from './types'

/**
 * API Client クラス
 *
 * Bearer token認証を含むfetchラッパー。
 * localStorageでトークンを永続化。
 */
export class ApiClient {
  private baseUrl: string
  private token: string | null = null

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl
    this.token = localStorage.getItem('noah_token')
  }

  /**
   * トークンを設定
   */
  setToken(token: string): void {
    this.token = token
    localStorage.setItem('noah_token', token)
  }

  /**
   * トークンをクリア
   */
  clearToken(): void {
    this.token = null
    localStorage.removeItem('noah_token')
  }

  /**
   * 認証済みfetchラッパー
   */
  private async fetch<T>(path: string, init?: RequestInit): Promise<T> {
    const headers = new Headers(init?.headers)

    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`)
    }

    const res = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers,
    })

    // 401エラーの場合は自動的にログアウト
    if (res.status === 401) {
      this.clearToken()
      throw new Error('Unauthorized')
    }

    if (!res.ok) {
      const body = await res.json().catch(() => null)
      const message = body?.error || `API error: ${res.status}`
      throw new Error(message)
    }

    return res.json()
  }

  /**
   * コメント一覧を取得
   */
  async getComments(params: {
    startDate?: string
    endDate?: string
    limit?: number
    cursor?: string
  }): Promise<CommentListResponse> {
    const query = new URLSearchParams()

    if (params.startDate) query.set('start_date', params.startDate)
    if (params.endDate) query.set('end_date', params.endDate)
    if (params.limit) query.set('limit', String(params.limit))
    if (params.cursor) query.set('cursor', params.cursor)

    const queryString = query.toString()
    const path = queryString ? `/comments?${queryString}` : '/comments'

    return this.fetch<CommentListResponse>(path)
  }

  /**
   * 画像URLを生成（認証付きfetchで取得する必要あり）
   */
  getImageUrl(key: string): string {
    return `${this.baseUrl}/images/${encodeURIComponent(key)}`
  }

  /**
   * 画像をfetchして Blob URL を生成
   */
  async fetchImageBlob(key: string): Promise<string> {
    const headers = new Headers()

    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`)
    }

    const res = await fetch(this.getImageUrl(key), { headers })

    if (!res.ok) {
      throw new Error(`Failed to fetch image: ${res.status}`)
    }

    const blob = await res.blob()
    return URL.createObjectURL(blob)
  }

  /**
   * アルバム一覧を取得
   */
  async getAlbums(params: {
    startDate: string
    endDate: string
  }): Promise<AlbumListResponse> {
    const query = new URLSearchParams()
    query.set('start_date', params.startDate)
    query.set('end_date', params.endDate)

    return this.fetch<AlbumListResponse>(`/albums?${query.toString()}`)
  }

  /**
   * アルバムをダウンロード
   */
  async downloadAlbum(albumId: string): Promise<AlbumDownloadResponse> {
    return this.fetch<AlbumDownloadResponse>(`/albums/${albumId}/download`, {
      method: 'POST',
    })
  }

  /**
   * 保存済みアルバム一覧を取得
   */
  async getSavedAlbums(): Promise<SavedAlbumListResponse> {
    return this.fetch<SavedAlbumListResponse>('/albums/saved')
  }

  /**
   * アルバムの写真一覧を取得
   */
  async getAlbumPhotos(albumId: string): Promise<AlbumPhotoListResponse> {
    return this.fetch<AlbumPhotoListResponse>(`/albums/${albumId}/photos`)
  }

  /**
   * ダウンロードジョブのステータスを取得
   */
  async getDownloadJobStatus(jobId: string): Promise<JobStatusResponse> {
    return this.fetch<JobStatusResponse>(`/albums/jobs/${jobId}`)
  }

  /**
   * 保護者連絡帳を同期
   */
  async syncParentComments(params: {
    startDate: string
    endDate: string
  }): Promise<ParentCommentSyncResponse> {
    return this.fetch<ParentCommentSyncResponse>('/parent-comments/sync', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        start_date: params.startDate,
        end_date: params.endDate,
      }),
    })
  }

  /**
   * 保護者連絡帳コメント一覧を取得（最新順・カーソルページネーション）
   */
  async getParentComments(params: {
    limit?: number
    cursor?: string
  }): Promise<ParentCommentListResponse> {
    const query = new URLSearchParams()

    if (params.limit) query.set('limit', String(params.limit))
    if (params.cursor) query.set('cursor', params.cursor)

    const queryString = query.toString()
    const path = queryString ? `/parent-comments?${queryString}` : '/parent-comments'

    return this.fetch<ParentCommentListResponse>(path)
  }

  /**
   * プロフィールを取得
   */
  async getProfile(): Promise<ProfileResponse> {
    return this.fetch<ProfileResponse>('/profile')
  }

  /**
   * 家族情報を同期
   */
  async syncFamily(): Promise<SyncFamilyResponse> {
    return this.fetch<SyncFamilyResponse>('/profile/sync-family', {
      method: 'POST',
    })
  }

  /**
   * 資格情報ステータスを取得
   */
  async getCredentialStatus(): Promise<CredentialStatusResponse> {
    return this.fetch<CredentialStatusResponse>('/settings/credentials')
  }

  /**
   * 資格情報を保存
   */
  async saveCredentials(params: {
    codmonId: string
    codmonPassword: string
  }): Promise<SaveCredentialResponse> {
    return this.fetch<SaveCredentialResponse>('/settings/credentials', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        codmon_id: params.codmonId,
        codmon_password: params.codmonPassword,
      }),
    })
  }

  /**
   * 資格情報を削除
   */
  async deleteCredentials(): Promise<DeleteCredentialResponse> {
    return this.fetch<DeleteCredentialResponse>('/settings/credentials', {
      method: 'DELETE',
    })
  }

  /**
   * LINE チャットへテキストを送信
   */
  async sendLineMessage(params: {
    to: string
    text: string
  }): Promise<LineNotifyResponse> {
    return this.fetch<LineNotifyResponse>('/line/notify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: params.to,
        text: params.text,
      }),
    })
  }
}

/**
 * シングルトンインスタンス
 */
export const apiClient = new ApiClient()
